/* eslint-disable @typescript-eslint/naming-convention */
import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { datadogRum } from '@datadog/browser-rum';
import { DateFormat } from '@app/constants';
import { AuthService, AuthenticationInfo, FlyoutService, NavigationService } from '@app/core/services';
import { HeaderLayoutElementsEnum } from '@app/enums';
import { GlobalUnitService } from '@app/shared/services';
import { toDateFormat } from '@app/utils';
import { environment } from '@environments/environment';
import { ClickToCopyDirective } from '../../directives/click-to-copy.directive';

interface DDContext {
	session_id: string;
	application_id: string;
}

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.less'],
    standalone: true,
    imports: [
        MatToolbarModule,
        NgIf,
        MatButtonModule,
        MatIconModule,
        MatMenuModule,
		MatTooltipModule,
        ClickToCopyDirective,
        AsyncPipe,
    ],
})
export class HeaderComponent implements OnInit {
	unitId: string;
	unitUnitName: string;
	fullName: string;
	email: string;
	activeLayoutElements: HeaderLayoutElementsEnum[] = [];
	buildDate: string = toDateFormat(environment.BUILD_DATE, DateFormat.shortDateTime) || environment.BUILD_DATE;
	copyBuildDate: string = toDateFormat(environment.BUILD_DATE, 'MM/DD/YYYY hh:mm:ss A') || environment.BUILD_DATE;
	buildId: string = environment.BUILD_ID;
	buildInfoString = `Build ID: ${this.buildId}\nBuild Date: ${this.copyBuildDate}`;
	ddContext: DDContext = undefined;
	enableChangeUnit = false;

	public layoutElements = HeaderLayoutElementsEnum;
	readonly #destroyRef = inject(DestroyRef);

	constructor(
		private readonly authService: AuthService,
		private readonly navigationService: NavigationService,
		private readonly flyoutService: FlyoutService,
		private readonly globalUnitService: GlobalUnitService,
	) {
		this.navigationService.headerItems$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((headerLayoutElements) => {
			this.activeLayoutElements = headerLayoutElements;
		});
	}

	flyoutOpen$ = this.flyoutService.open$;

	ngOnInit(): void {
		this.authService.authentication$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((authentication: AuthenticationInfo) => {
			this.unitId = authentication?.unitId;
			this.email = authentication?.email;
			this.fullName = authentication?.fullName;
			this.unitUnitName = authentication?.unitName;

			this.setChangeUnitEnabled();
		});

		this.ddContext = datadogRum?.getInternalContext?.();
	}

	openSideNav(): void {
		this.navigationService.toggleSideNav();
	}

	changeUnit(): void {
		this.navigationService.openSwitchUnit();
	}

	logout(): void {
		this.authService.logOut();
	}

	elementActive(element: HeaderLayoutElementsEnum): boolean {
		return this.activeLayoutElements.includes(element);
	}

	onSettingsButtonClick(): void {
		this.navigationService.openSystemSettings();
	}

	onFeeManagementButtonClick(): void {
		this.navigationService.openFeeManagement();
	}

	private setChangeUnitEnabled(): void {
		if (!this.unitId) {
			this.enableChangeUnit = false;
			return;
		}

		this.globalUnitService.getListByUser().subscribe((response) => {
			this.enableChangeUnit = response.data.some((unit) => unit.UnitId !== this.unitId);
		});
	}
}
